import { apiCall } from '@/lib/api';
import { createMutation, createQuery, useQueryClient } from '@tanstack/solid-query';
import type { RespondToPollBody, UpdatePollBody } from './api';
import {
  addPollOption,
  closePoll,
  deletePoll,
  getPoll,
  getPollResults,
  getPollViewerCandidates,
  getPollViewers,
  listPolls,
  publishPoll,
  replacePollViewers,
  respondToPoll,
  retractPollResponse,
  updatePoll,
} from './api';

const pollsKey = ['polls'] as const;
const pollKey = (id: string) => ['polls', id] as const;
const invalidate = (qc: ReturnType<typeof useQueryClient>, id?: string) => {
  qc.invalidateQueries({ queryKey: pollsKey });
  if (id) qc.invalidateQueries({ queryKey: pollKey(id) });
};

export const usePolls = () =>
  createQuery(() => ({
    queryKey: [...pollsKey, 'list'] as const,
    queryFn: () => apiCall(listPolls, { query: { limit: 100 } }),
  }));

export const usePoll = (pollId: () => string) =>
  createQuery(() => ({
    queryKey: pollKey(pollId()),
    queryFn: () => apiCall(getPoll, { params: { pollId: pollId() } }),
  }));

export const usePollResults = (pollId: () => string) =>
  createQuery(() => ({
    queryKey: [...pollKey(pollId()), 'results'] as const,
    queryFn: () => apiCall(getPollResults, { params: { pollId: pollId() } }),
  }));

export const useUpdatePoll = (pollId: string) => {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: (body: UpdatePollBody) => apiCall(updatePoll, { params: { pollId }, body }),
    onSuccess: () => invalidate(qc, pollId),
  }));
};

export const usePublishPoll = (pollId: string) => {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: () => apiCall(publishPoll, { params: { pollId } }),
    onSuccess: () => invalidate(qc, pollId),
  }));
};

export const useClosePoll = (pollId: string) => {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: () => apiCall(closePoll, { params: { pollId } }),
    onSuccess: () => invalidate(qc, pollId),
  }));
};

export const useDeletePoll = (pollId: string) => {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: () => apiCall(deletePoll, { params: { pollId } }),
    onSuccess: () => invalidate(qc),
  }));
};

export const useRespondToPoll = (pollId: string) => {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: (body: RespondToPollBody) => apiCall(respondToPoll, { params: { pollId }, body }),
    onSuccess: () => invalidate(qc, pollId),
  }));
};

export const useRetractPollResponse = (pollId: string) => {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: () => apiCall(retractPollResponse, { params: { pollId } }),
    onSuccess: () => invalidate(qc, pollId),
  }));
};

export const useAddPollOption = (pollId: string, fieldId: string) => {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: (label: string) =>
      apiCall(addPollOption, { params: { pollId, fieldId }, body: { label } }),
    onSuccess: () => invalidate(qc, pollId),
  }));
};

export const usePollViewers = (pollId: () => string, enabled = true) =>
  createQuery(() => ({
    enabled,
    queryKey: [...pollKey(pollId()), 'viewers'] as const,
    queryFn: () => apiCall(getPollViewers, { params: { pollId: pollId() } }),
  }));

export const usePollViewerCandidates = (pollId: () => string, enabled = true) =>
  createQuery(() => ({
    enabled,
    queryKey: [...pollKey(pollId()), 'viewer-candidates'] as const,
    queryFn: () => apiCall(getPollViewerCandidates, { params: { pollId: pollId() } }),
  }));

export const useReplacePollViewers = (pollId: string) => {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: (userIds: string[]) =>
      apiCall(replacePollViewers, { params: { pollId }, body: { userIds } }),
    onSuccess: () => invalidate(qc, pollId),
  }));
};
